import { FaGithub, FaChevronLeft, FaChevronRight, FaEye, FaArrowUpRightFromSquare, FaHammer } from 'react-icons/fa6';
import { FC, useState } from 'react';
import './Projects.css';

interface Project {
  title: string;
  description: string;
  tags: string[];
  github: string;
  demo: string;
  pattern: string; // CSS class for cover motif
  coverImage?: string; // Optional cover image URL
  recruiterMessage?: string; // Optional message for recruiters
  inDevelopment?: boolean; // Indique si le projet est en cours de développement
  isSuspended?: boolean; // Indique si le projet est suspendu
}

const projects: Project[] = [
  {
    title: 'Portfolio Personnel',
    description: 'Mon portfolio personnel présentant mes compétences et projets. Développé avec React, TypeScript et Tailwind CSS.',
    tags: ['React', 'TypeScript', 'Tailwind CSS'],
    github: '',
    demo: '',
    pattern: 'pattern-stripes',
    coverImage: '/assets/raider.png',
  },
  {
    title: 'Fidelix',
    description: 'Application mobile qui simplifie les procédures d\'achat sur Alibaba, Shein et Temu en un seul endroit.',
    tags: ['React Native', 'Firebase', 'API Alibaba'],
    github: '',
    demo: 'https://play.google.com/store/apps/details?id=com.raidercorporation.fidelix',
    pattern: 'pattern-dots',
    coverImage: '/assets/fidelix.png',
    inDevelopment: true, // Nouvelle propriété pour indiquer que le projet est en développement
  },
  {
    title: 'Lafia Trafics',
    description: 'Plateforme de transit avec tunnel de vente WhatsApp pour maximiser les conversions.',
    tags: ['WordPress', 'Elementor'],
    github: '',
    demo: 'https://lafiatrafics.com',
    recruiterMessage: 'Découvrez comment ce tunnel a augmenté les conversions de 40%',
    pattern: 'pattern-grid',
    coverImage: '/assets/lafia.png',
  },
  {
    title: 'Portfolio Graphiste',
    description: 'Portfolio en ligne mettant en valeur des créations graphiques et identités visuelles.',
    tags: ['UI/UX Design', 'Visme'],
    github: '',
    demo: 'https://yproduction-226.html-5.me/',
    recruiterMessage: 'Découvrez une sélection de projets graphiques et identités visuelles',
    pattern: 'pattern-diagonal',
    coverImage: '/assets/yp.png',
  },
  {
    title: 'Kono Aliments',
    description: 'Solutions nutritionnelles de haute qualité pour optimiser la santé et la productivité de votre bétail et de votre volaille.',
    tags: ['Site Vitrine'],
    github: '',
    demo: 'https://konoaliments.com/',
    recruiterMessage: 'Solution clé pour les éleveurs cherchant à optimiser la santé de leur cheptel et améliorer leurs rendements',
    pattern: 'pattern-waves',
    coverImage: '/assets/kono.png',
  },
  {
    title: 'Oasis Care Group',
    description: 'Distribution de matériels médicaux et produits de santé naturels pour la réparation et le soin des peaux sensibles et abîmées.',
    tags: ['Site Vitrine', 'WordPress'],
    github: '',
    isSuspended: true,
    demo: 'https://oasiscaregroup.pro',
    recruiterMessage: 'Solutions innovantes pour une peau saine et régénérée',
    pattern: 'pattern-triangles',
    coverImage: '/assets/oasis.jfif',
  },
];

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: FC<ProjectCardProps> = ({ project }) => {
  // Image de prévisualisation (sous le titre)
  const previewImage = getProjectPreviewImage(project.title);
  
  // Image de couverture (pour la couverture du livre)
  const hasCoverImage = !!project.coverImage;

  return (
    <div className="book">
{/* Badge En développement - dans la couverture du livre */}
      {project.inDevelopment && (
        <div className="book-cover-badge">
          <div className="bg-yellow-500 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1 font-bold border border-white">
            <FaHammer className="text-[8px]" />
            <span className="text-black">BÊTA</span>
          </div>
        </div>
      )}
      {/* Badge Suspendu - dans la couverture du livre */}
      {project.isSuspended && (
        <div className="book-cover-badge">
          <div className="bg-red-500 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1 font-bold border border-white">
            <span>SUSPENDU</span>
          </div>
        </div>
      )}
      {/* Particules de poussière */}
      {[...Array(11)].map((_, i) => (
        <div key={i} className="dust"></div>
      ))}
      <div className="inner">
        <h3 className="text-xl font-bold mb-2 flex items-center gap-2 text-black">
          {project.title}
          {project.isSuspended && (
            <div className="relative group">
              <div className="relative">
                {/* Flèches animées */}
                <div className="absolute -top-1 -right-0.5 animate-bounce">
                  <div className="w-1.5 h-1.5 border-t-2 border-r-2 border-black transform rotate-60"></div>
                </div>
                <div className="absolute -bottom-1 -left-1 animate-bounce" style={{ animationDelay: '0.3s' }}>
                  <div className="w-2 h-2 border-b-2 border-l-2 border-black transform -rotate-135"></div>
                </div>
                
                {/* Icône d'information */}
                <div className="w-4 h-4 bg-red-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold cursor-help hover:bg-red-700 animate-pulse hover:animate-none">i</div>
              </div>
              
              {/* Infobulle */}
              <div className="absolute z-10 hidden group-hover:block w-56 bg-black text-white text-xs rounded p-2 -left-28 -top-12">
                Projet suspendu - Le client a choisi de ne pas renouveler son hébergement et son nom de domaine.
              </div>
            </div>
          )}
        </h3>
        {previewImage && (
          <div className="mb-3 rounded-lg shadow-md bg-gray-100 flex items-center justify-center" style={{ height: '160px', overflow: 'hidden' }}>
            <img 
              src={previewImage} 
              alt={`Prévisualisation de ${project.title}`} 
              className="h-full w-auto max-w-full object-contain"
              style={{ maxHeight: '160px' }}
              onError={(e) => {
                // En cas d'erreur de chargement de l'image, on la cache
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        )}
        <p className="text-sm mb-3">{project.description}</p>
        <div className="flex flex-wrap justify-center gap-2 mb-3">
          {project.tags.map((tag, i) => (
            <span 
              key={i} 
              className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex gap-3 mt-auto">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 transition-colors flex items-center text-sm"
            >
              <FaGithub className="w-4 h-4 mr-1" /> Code
            </a>
          )}
          {project.demo && project.demo.includes('play.google.com') ? (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center w-full"
            >
              <img 
                src="/assets/download.png" 
                alt="Télécharger sur le Play Store" 
                className="h-10 w-auto transition-all duration-300 transform hover:scale-105 hover:drop-shadow-lg"
              />
            </a>
          ) : project.recruiterMessage ? (
            <div className="relative group inline-block mx-auto">
              {project.title !== 'Fidelix' ? (
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-7 px-3 text-xs bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-md gap-1.5 cursor-pointer transition-all hover:from-green-600 hover:to-green-700 hover:shadow-md hover:scale-105"
                  style={{
                    minWidth: '110px',
                    textDecoration: 'none'
                  }}
                >
                  <FaArrowUpRightFromSquare style={{ width: '10px', height: '10px' }} />
                  {project.demo === '#' ? 'En cours' : 'Visiter le site'}
                </a>
              ) : (
                <button 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '28px',
                    padding: '0 0.75rem',
                    fontSize: '0.7rem',
                    background: 'linear-gradient(to right, #1f2937, #111827)',
                    color: 'white',
                    borderRadius: '6px',
                    gap: '0.4rem',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '110px',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <FaEye style={{ width: '10px', height: '10px' }} />
                  Voir mon travail
                </button>
              )}
              <div className="invisible group-hover:visible absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-gray-800 text-white text-sm px-3 py-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                {project.recruiterMessage}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-800"></div>
              </div>
            </div>
          ) : project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '26px',
                padding: '0.25rem 0.6rem',
                fontSize: '0.65rem',
                backgroundColor: project.title === 'Fidelix' ? '#dbeafe' : '#f3f4f6',
                color: project.title === 'Fidelix' ? '#1e40af' : '#1f2937',
                borderRadius: '4px',
                gap: '0.25rem',
                textDecoration: 'none',
                transition: 'all 0.2s',
                minWidth: '80px',
              }}
              onMouseOver={(e) => {
                if (project.title !== 'Fidelix') {
                  e.currentTarget.style.backgroundColor = '#10b981';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                } else {
                  e.currentTarget.style.backgroundColor = '#bfdbfe';
                }
              }}
              onMouseOut={(e) => {
                if (project.title !== 'Fidelix') {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#1f2937';
                  e.currentTarget.style.transform = 'none';
                } else {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                }
              }}
            >
              <FaArrowUpRightFromSquare style={{ width: '9px', height: '9px', position: 'relative', top: '-0.5px' }} />
              Voir la démo
            </a>
          )}
        </div>
      </div>
      <div className={`cover ${project.pattern} ${hasCoverImage ? 'has-image' : ''}`}>
        {hasCoverImage && (
          <img src={project.coverImage} alt={project.title} className="cover-image" />
        )}
        <div className="cover-content">
          {!hasCoverImage && (
            <div className="cover-icons">
              <FaEye className="icon" />
            </div>
          )}
          <p className="cover-title">{project.title}</p>
          <div className="cover-badge">Voir le projet</div>
        </div>
        <div className="cover-shine" />
      </div>
    </div>
  );
};

const Projects: FC = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const total = projects.length;

  // Calcul de l'index du projet suivant/précédent selon la direction
  const getNextIndex = (dir: 'left' | 'right') => {
    if (dir === 'left') return (current - 1 + total) % total;
    return (current + 1) % total;
  };

  // Handler pour navigation
  const handleNav = (dir: 'left' | 'right') => {
    if (isAnimating) return;
    setDirection(dir);
    setIsAnimating(true);
  };

  // Handler pour dots
  const handleDot = (i: number) => {
    if (isAnimating || i === current) return;
    setDirection(i > current ? 'right' : 'left');
    setIsAnimating(true);
  };

  // Callback à la fin de l'animation
  const handleAnimationEnd = () => {
    if (!direction) return;
    setCurrent(getNextIndex(direction));
    setIsAnimating(false);
    setDirection(null);
  };

  // Index du projet "entrant"
  const nextIndex = direction ? getNextIndex(direction) : current;

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 projects-section">
      <h2 className="text-4xl font-bold text-center text-green-400 mb-4">Mes Projets</h2>
      <p className="text-center text-gray-300 max-w-3xl mx-auto mb-10 text-lg">
        Découvrez une sélection de mes réalisations professionnelles, du développement web sur mesure à la création d'applications mobiles, en passant par des solutions WordPress. Certains projets clients ne sont pas disponibles pour des raisons de confidentialité.
      </p>

      <div className="carousel">
        <button className="nav-btn left" onClick={() => handleNav('left')} aria-label="Projet précédent" disabled={isAnimating}>
          <FaChevronLeft />
        </button>

        {/* Structure pour effet page turn : 2 cartes superposées */}
        <div className="carousel-books">
          <div
            className={`carousel-book ${isAnimating && direction === 'right' ? 'page-turn-out-right' : ''} ${isAnimating && direction === 'left' ? 'page-turn-out-left' : ''}`}
            style={{ zIndex: isAnimating ? 1 : 2 }}
            onAnimationEnd={isAnimating ? handleAnimationEnd : undefined}
          >
            <ProjectCard project={projects[current]} />
          </div>
          {isAnimating && (
            <div
              className={`carousel-book ${direction === 'right' ? 'page-turn-in-right' : ''} ${direction === 'left' ? 'page-turn-in-left' : ''}`}
              style={{ zIndex: 2 }}
            >
              <ProjectCard project={projects[nextIndex]} />
            </div>
          )}
        </div>

        <button className="nav-btn right" onClick={() => handleNav('right')} aria-label="Projet suivant" disabled={isAnimating}>
          <FaChevronRight />
        </button>
      </div>

      <div className="mt-4 text-center text-sm text-gray-400">
        {current + 1} / {total}
      </div>

      <div className="dots" role="tablist" aria-label="Projets">
        {projects.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === current ? 'active' : ''}`}
            onClick={() => handleDot(i)}
            aria-label={`Aller au projet ${i + 1}`}
            aria-selected={i === current}
            role="tab"
            disabled={isAnimating}
          />
        ))}
      </div>

      
    </div>
  );
};

// Fonction utilitaire pour obtenir le chemin de l'image de prévisualisation
const getProjectPreviewImage = (title: string) => {
  switch(title) {
    case 'Portfolio Personnel':
      return '/assets/raiderpage.PNG';
    case 'Fidelix':
      return '/assets/fiddd.png';
    case 'Lafia Trafics':
      return '/assets/lafiapage.PNG';
    case 'Portfolio Graphiste':
      return '/assets/yppage.PNG';
    case 'Kono Aliments':
      return '/assets/konopage.PNG';
    case 'Oasis Care Group':
      return '/assets/oasispage.PNG';
    default:
      return '/assets/placeholder.png';
  }
};

export default Projects;




